/**
 * Height values for each divider preset.
 * Uses responsive clamp() values for fluid sizing.
 */
export const DIVIDER_PRESET_HEIGHTS = {
  'wave-smooth': 'clamp(40px, 5vw, 80px)',
  'wave-layered': 'clamp(60px, 7vw, 100px)',
  'wave-asymmetric': 'clamp(40px, 5vw, 80px)',
  'tilt': 'clamp(40px, 5vw, 80px)',
  'arrow': 'clamp(40px, 5vw, 80px)',
  'curve-concave': 'clamp(40px, 5vw, 80px)',
  'curve-convex': 'clamp(40px, 5vw, 80px)',
  'curve-double': 'clamp(40px, 5vw, 80px)',
  'sweep': 'clamp(40px, 5vw, 80px)',
  'spill': 'clamp(60px, 8vw, 120px)',
  'triangle': 'clamp(40px, 5vw, 80px)',
  'peaks': 'clamp(60px, 10vw, 160px)',
  'steps': 'clamp(60px, 10vw, 160px)',
  'fade': 'clamp(60px, 8vw, 120px)'
};

/**
 * Get the base height for a divider preset.
 * @param {string} preset - The divider preset name
 * @returns {string} The CSS height value with clamp()
 */
export function getDividerHeightForPreset(preset) {
  return DIVIDER_PRESET_HEIGHTS[preset] || DIVIDER_PRESET_HEIGHTS['wave-smooth'];
}

/** Params that affect vertical extent per preset */
const PRESET_HEIGHT_PARAMS = {
  'wave-smooth': ['depth'],
  'wave-layered': ['depth'],
  'wave-asymmetric': ['depth'],
  'tilt': ['steepness'],
  'arrow': ['depth'],
  'curve-concave': ['depth'],
  'curve-convex': ['depth'],
  'curve-double': ['depth'],
  'sweep': ['steepness', 'depth'],
  'spill': ['depth'],
  'triangle': ['depth'],
  'peaks': ['depth'],
  'steps': ['depth'],
  'fade': []
};

/** Slider midpoint; at this value we want no scaling (scale = 1). */
const PARAM_DEFAULT = 50;
/** Scale sensitivity */
const SCALE_SENSITIVITY = 0.4;
/** Clamp bounds */
const SCALE_MIN = 0.85;
const SCALE_MAX = 1.6;

function getDividerHeightScale(divider) {
  const params = PRESET_HEIGHT_PARAMS[divider.preset] || [];
  if (!params.length) return 1;

  let delta = 0;
  for (const key of params) {
    const v = divider[key] != null ? divider[key] : PARAM_DEFAULT;
    delta += ((v - PARAM_DEFAULT) / 100) * SCALE_SENSITIVITY;
  }
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, 1 + delta));
}

/**
 * Spill viewBox height ratio calculation.
 * @param {number} depth - The depth parameter for the spill divider
 * @returns {number} The height ratio multiplier
 */
function getSpillViewHeightRatio(depth) {
  const scale = depth / 50; // 0 to 2
  const TOP_MARGIN = 10;
  const minY = 70 - 65 * scale - TOP_MARGIN;
  const pathBottom = 120;
  const viewHeight = pathBottom - minY;
  return viewHeight / 120;
}

/**
 * Get the reserved height for a section divider based on its config.
 * Use this for section gap minHeight to prevent content overlap.
 * @param {object} divider - The divider configuration object
 * @returns {string|null} The CSS height value or null if no divider
 */
export function getDividerHeight(divider) {
  if (!divider || !divider.preset) return null;

  const base = getDividerHeightForPreset(divider.preset);

  if (divider.preset === 'spill') {
    const depth = divider.depth != null ? divider.depth : 50;
    const ratio = getSpillViewHeightRatio(depth);
    if (ratio <= 1) return base;
    return `calc(${base} * ${ratio})`;
  }

  const scale = getDividerHeightScale(divider);
  if (scale === 1) return base;
  return `calc(${base} * ${scale})`;
}
